/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../widgets/livewidget/nls/StringResources',
	'../api/SlotActionsProviderAPI',
	'../../../../apiHelpers/SlotAPIHelper'
], (APIFactory, stringResources, SlotActionsProviderAPI, SlotAPIHelper) => {

	class BinAction {
		static DEFAULT_NUMBER_OF_BINS = 10

		constructor({ content, features }) {
			this.content = content;
			this.features = features;
			this.dashboard = this.features['Dashboard.API'];
			this.features['SlotActions'].registerProvider('autoBinAction', this.getAPI());
			this.logger = this.dashboard.getGlassCoreSvc('.Logger');
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [ SlotActionsProviderAPI ]);
			}
			return this.api;
		}

		_isOlap(dataSource, dataItem) {
			return !!(dataSource.isOlapPackage() || dataItem && dataItem.getMetadataColumn().isOlapColumn());
		}

		_supportAction(slot, itemIndex) {
			// Olap sources are unsupported, for now.
			const visualization = this.features['Visualization'];
			const dataSource = visualization.getDataSource();
			const dataItem =  slot.getDataItemList()[itemIndex];
			const isOlap = this._isOlap(dataSource, dataItem);
			let unsupportedSource = !dataSource || isOlap;
			const config = visualization.getDefinition().getProperty('binningConfig');
			const multiSelect = Array.isArray(itemIndex) && itemIndex.length > 1;
			let supported = !multiSelect &&
				itemIndex !== -1 &&
				(!config || config.auto !== false) &&
				!unsupportedSource &&
				!(SlotAPIHelper.isMultiMeasuresSeriesSlot(slot) || SlotAPIHelper.isMultiMeasuresValueSlot(slot) ) &&
				SlotAPIHelper.doesDataItemSupportBinning(slot, dataItem);
			if (supported) {
				const binning = dataItem.getBinning();
				const columnId = binning ? binning.baseColumnId : dataItem.getColumnId();
				const data = {
					basedOnMoserObjectId: columnId
				};
				supported = this._getCustomBinUI().validateCustomBinning(dataSource.getId(), columnId, data);
			}
			return supported;
		}

		_getViewState(slot, itemIndex) {
			const definition = this.features['Visualization'].getDefinition();

			if (slot) {
				// @todo get slot from visualizationAPI and use new SLOTAPI
				const config = definition.getProperty('binningConfig');
				const dataItem = slot.getDataItemList()[itemIndex];
				if (dataItem) {
					const viewState = {
						height: 280,
						width: 400, // those are hints only; for the placement of the toolbar.,
						defaultNumberOfBins: (config && config.defaultNumberOfBins) || BinAction.DEFAULT_NUMBER_OF_BINS,
						binningStateCb: this._binningStateCallback.bind(this, dataItem)
					};
					return viewState;
				}
			}
			return null;
		}

		_binningStateCallback(dataItem) {
			const binning = dataItem.getBinning();
			if (binning) {
				const mdColumn = dataItem && dataItem.getMetadataColumn();
				const bins = this._getCustomBinUI().getCustomBinning(mdColumn.getSourceId(), binning.columnId);
				return bins ? { ...binning, bins: bins.numberOfBins } : binning;
			}
			return binning;
		}

		/**
		 * Find the correct data item index
		 * @param {Object} slot - slot api for slot we're checking against
		 * @return {Integer || undefined} Returns the index of the first data item in this slot that has the appropriate type for autobinning or undefined if none is found
		 */
		_findDataItemIndex(slot) {
			let index = -1;
			// when this path is triggered from rclick on axis title, we get the wrong mapindex for the data item
			// index is null but we have data items, maybe 1st dataitem isnt the right one which is the behaviour we saw by letting the incorrect index propagate forward
			// works fine if its from data slots view, mapindex comes in defined and correct
			if (slot.getDataItemList().length > 0) {
				const dataItems = slot.getDataItemList();
				index = dataItems.findIndex((dataItem) => {
					if (dataItem.getBinning()) {
						return true;
					} else {
						const metaDataColumn = dataItem.getMetadataColumn();
						if (metaDataColumn && metaDataColumn.getType() === 'fact') {
							return true;
						}
					}
				});
			}
			return index;
		}

		getSlotActionList(slotId, itemIndex) {
			const visualization = this.features['Visualization'];
			const slot = visualization.getSlots().getSlot(slotId);
			if (itemIndex === null || itemIndex === undefined) {
				itemIndex = this._findDataItemIndex(slot);
			}
			const dataItem = slot.getDataItemList()[itemIndex];
			if (slot && this._supportAction(slot, itemIndex)) {
				let viewOptions = this._getViewState(slot, itemIndex);
				const autoBinIcon = this.features['Dashboard.Icons'].getIcon('dashboard-autobin');
				return [{
					name: 'autoBinAction',
					targetCaption: dataItem && dataItem.getLabel(),
					label: stringResources.get('autoBinActionText'),
					icon: autoBinIcon.id,
					type: 'NextView',
					view: {
						module: 'dashboard-analytics/dialogs/AutobinDialog',
						state: viewOptions
					},
					actions: {
						apply: this._applyBinning.bind(this, slot, itemIndex)
					}
				}];
			}
			return [];
		}

		_getMoserDataSources() {
			if (!this._moserDataSources) {
				this._moserDataSources = this.features['DataSources.moser'];
			}
			return this._moserDataSources;
		}

		_ensureTemporaryModule(sourceId) {
			const dataSources = this._getMoserDataSources();
			return dataSources.ensureTemporaryModule(sourceId);
		}

		_applyBinningState(slot, itemIndex, binningState, token){
			const dataItem = slot.getDataItemList()[itemIndex];
			const mdColumn = dataItem && dataItem.getMetadataColumn();
			if (binningState) {
				const binning = dataItem.getBinning();
				return this.createCustomBinning(dataItem, mdColumn, binningState.numberOfBins, token).then((customBinData) =>{
					if (customBinData){
						dataItem.setBinning({ ...binning, bins: binningState.numberOfBins, label:customBinData.getLabel(), columnId: customBinData.getId() }, token);
					}
				});
			} else {
				return this.removeCustomBinning(mdColumn, dataItem, token).then(() => {
					dataItem.setBinning(null, token);
				});
			}
		}

		_applyBinning(slot, itemIndex, binningState) {
			const token = this.features['Dashboard.Transaction'].startTransaction();
			this._applyBinningState(slot, itemIndex, binningState, token).finally(() => {
				this.features['Dashboard.Transaction'].endTransaction(token);
			});
		}

		_updateCustomBinningSpec(numberOfBins, customBinData) {
			customBinData.numberOfBins = numberOfBins.toString();
			return customBinData;
		}

		_getCustomBinningSpec(dataItem) {
			const binning = dataItem.getBinning();
			if (binning){
				return {
					basedOnMoserObjectId: binning.baseColumnId
				};
			}
			return null;
		}

		_createCustomBinningSpec(numberOfBins){
			return {
				numberOfBins: numberOfBins.toString()
			};
		}

		createCustomBinning(dataItem, mdColumn, numberOfBins, token) {
			return this._ensureTemporaryModule(mdColumn.getSourceId()).then(() => {
				let columnId = dataItem.getColumnId();
				let data = this._createCustomBinningSpec(numberOfBins);
				let isEdit = false;

				const existingCustomBinData = this._getCustomBinningSpec(dataItem);
				if (existingCustomBinData) {
					data = this._updateCustomBinningSpec(numberOfBins, existingCustomBinData);
					isEdit = true;
				}
				return this._getCustomBinUI().createCustomBinning(mdColumn.getSourceId(), columnId, isEdit, data, token).then((customBinData) => {
					return Promise.resolve(customBinData);
				}).catch((error) => {
					this._handleMUIDialogErrors(error,'createCustomBinningErrorToast');
					return Promise.resolve(null);
				});
			});
		}

		_handleMUIDialogErrors(errors, msgKey) {
			const errorMessages = errors.messages || [];
			if (errorMessages.length > 0) {
				errorMessages.forEach((error) => {
					this.logger.error(error.messageHint, this);
				});
			} else {
				// need to handle unexpected errors too
				this.logger.error(errors, this);
			}
			const toastOptions = {
				type: 'error',
				preventDuplicates: true
			};
			this.dashboard.showToast(stringResources.get(msgKey), toastOptions);
		}

		_getCustomBinUI() {
			if (!this._uiDataSources) {
				this._uiDataSources = this.features['DataSources.CustomBinUI'];
			}
			return this._uiDataSources;
		}

		removeCustomBinning(mdColumn, dataItem, token) {
			const binning = dataItem.getBinning();
			if (binning) {
				return this._getCustomBinUI().removeCustomBinning(mdColumn.getSourceId(), dataItem.getColumnId(), token).then(() => {
					return true;
				}).catch((error) => {
					this._handleMUIDialogErrors(error,'removeCustomBinningErrorToast');
					return false;
				});
			}
			return Promise.resolve(false);
		}

	}

	return BinAction;

});
